import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Book, ChevronLeft, ChevronRight, BookOpen, Search, X, Volume2, VolumeX } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";

// Bible books structure with chapter counts
const BIBLE_BOOKS = {
  oldTestament: [
    { id: 1, key: "genesis", chapters: 50 },
    { id: 2, key: "exodus", chapters: 40 },
    { id: 3, key: "leviticus", chapters: 27 },
    { id: 4, key: "numbers", chapters: 36 },
    { id: 5, key: "deuteronomy", chapters: 34 },
    { id: 6, key: "joshua", chapters: 24 },
    { id: 7, key: "judges", chapters: 21 },
    { id: 8, key: "ruth", chapters: 4 },
    { id: 9, key: "1samuel", chapters: 31 },
    { id: 10, key: "2samuel", chapters: 24 },
    { id: 11, key: "1kings", chapters: 22 },
    { id: 12, key: "2kings", chapters: 25 },
    { id: 13, key: "1chronicles", chapters: 29 },
    { id: 14, key: "2chronicles", chapters: 36 },
    { id: 15, key: "ezra", chapters: 10 },
    { id: 16, key: "nehemiah", chapters: 13 },
    { id: 17, key: "esther", chapters: 10 },
    { id: 18, key: "job", chapters: 42 },
    { id: 19, key: "psalms", chapters: 150 },
    { id: 20, key: "proverbs", chapters: 31 },
    { id: 21, key: "ecclesiastes", chapters: 12 },
    { id: 22, key: "songofsolomon", chapters: 8 },
    { id: 23, key: "isaiah", chapters: 66 },
    { id: 24, key: "jeremiah", chapters: 52 },
    { id: 25, key: "lamentations", chapters: 5 },
    { id: 26, key: "ezekiel", chapters: 48 },
    { id: 27, key: "daniel", chapters: 12 },
    { id: 28, key: "hosea", chapters: 14 },
    { id: 29, key: "joel", chapters: 3 },
    { id: 30, key: "amos", chapters: 9 },
    { id: 31, key: "obadiah", chapters: 1 },
    { id: 32, key: "jonah", chapters: 4 },
    { id: 33, key: "micah", chapters: 7 },
    { id: 34, key: "nahum", chapters: 3 },
    { id: 35, key: "habakkuk", chapters: 3 },
    { id: 36, key: "zephaniah", chapters: 3 },
    { id: 37, key: "haggai", chapters: 2 },
    { id: 38, key: "zechariah", chapters: 14 },
    { id: 39, key: "malachi", chapters: 4 },
  ],
  newTestament: [
    { id: 40, key: "matthew", chapters: 28 },
    { id: 41, key: "mark", chapters: 16 },
    { id: 42, key: "luke", chapters: 24 },
    { id: 43, key: "john", chapters: 21 },
    { id: 44, key: "acts", chapters: 28 },
    { id: 45, key: "romans", chapters: 16 },
    { id: 46, key: "1corinthians", chapters: 16 },
    { id: 47, key: "2corinthians", chapters: 13 },
    { id: 48, key: "galatians", chapters: 6 },
    { id: 49, key: "ephesians", chapters: 6 },
    { id: 50, key: "philippians", chapters: 4 },
    { id: 51, key: "colossians", chapters: 4 },
    { id: 52, key: "1thessalonians", chapters: 5 },
    { id: 53, key: "2thessalonians", chapters: 3 },
    { id: 54, key: "1timothy", chapters: 6 },
    { id: 55, key: "2timothy", chapters: 4 },
    { id: 56, key: "titus", chapters: 3 },
    { id: 57, key: "philemon", chapters: 1 },
    { id: 58, key: "hebrews", chapters: 13 },
    { id: 59, key: "james", chapters: 5 },
    { id: 60, key: "1peter", chapters: 5 },
    { id: 61, key: "2peter", chapters: 3 },
    { id: 62, key: "1john", chapters: 5 },
    { id: 63, key: "2john", chapters: 1 },
    { id: 64, key: "3john", chapters: 1 },
    { id: 65, key: "jude", chapters: 1 },
    { id: 66, key: "revelation", chapters: 22 },
  ],
};

// Bible versions by language
const BIBLE_VERSIONS: Record<string, string> = {
  en: "KJV",
  ru: "SYNOD",
  es: "RV1960", // Reina-Valera 1960 (standard Spanish translation)
};

// Book name translations for display
const BOOK_DISPLAY_NAMES: Record<string, Record<string, string>> = {
  en: {
    genesis: "Genesis", exodus: "Exodus", leviticus: "Leviticus", numbers: "Numbers",
    deuteronomy: "Deuteronomy", joshua: "Joshua", judges: "Judges", ruth: "Ruth",
    "1samuel": "1 Samuel", "2samuel": "2 Samuel", "1kings": "1 Kings", "2kings": "2 Kings",
    "1chronicles": "1 Chronicles", "2chronicles": "2 Chronicles", ezra: "Ezra", nehemiah: "Nehemiah",
    esther: "Esther", job: "Job", psalms: "Psalms", proverbs: "Proverbs",
    ecclesiastes: "Ecclesiastes", songofsolomon: "Song of Solomon", isaiah: "Isaiah",
    jeremiah: "Jeremiah", lamentations: "Lamentations", ezekiel: "Ezekiel", daniel: "Daniel",
    hosea: "Hosea", joel: "Joel", amos: "Amos", obadiah: "Obadiah", jonah: "Jonah",
    micah: "Micah", nahum: "Nahum", habakkuk: "Habakkuk", zephaniah: "Zephaniah",
    haggai: "Haggai", zechariah: "Zechariah", malachi: "Malachi",
    matthew: "Matthew", mark: "Mark", luke: "Luke", john: "John", acts: "Acts",
    romans: "Romans", "1corinthians": "1 Corinthians", "2corinthians": "2 Corinthians",
    galatians: "Galatians", ephesians: "Ephesians", philippians: "Philippians",
    colossians: "Colossians", "1thessalonians": "1 Thessalonians", "2thessalonians": "2 Thessalonians",
    "1timothy": "1 Timothy", "2timothy": "2 Timothy", titus: "Titus", philemon: "Philemon",
    hebrews: "Hebrews", james: "James", "1peter": "1 Peter", "2peter": "2 Peter",
    "1john": "1 John", "2john": "2 John", "3john": "3 John", jude: "Jude", revelation: "Revelation"
  },
  ru: {
    genesis: "Бытие", exodus: "Исход", leviticus: "Левит", numbers: "Числа",
    deuteronomy: "Второзаконие", joshua: "Иисус Навин", judges: "Судьи", ruth: "Руфь",
    "1samuel": "1 Царств", "2samuel": "2 Царств", "1kings": "3 Царств", "2kings": "4 Царств",
    "1chronicles": "1 Паралипоменон", "2chronicles": "2 Паралипоменон", ezra: "Ездра", nehemiah: "Неемия",
    esther: "Есфирь", job: "Иов", psalms: "Псалтирь", proverbs: "Притчи",
    ecclesiastes: "Екклесиаст", songofsolomon: "Песнь Песней", isaiah: "Исаия",
    jeremiah: "Иеремия", lamentations: "Плач Иеремии", ezekiel: "Иезекииль", daniel: "Даниил",
    hosea: "Осия", joel: "Иоиль", amos: "Амос", obadiah: "Авдий", jonah: "Иона",
    micah: "Михей", nahum: "Наум", habakkuk: "Аввакум", zephaniah: "Софония",
    haggai: "Аггей", zechariah: "Захария", malachi: "Малахия",
    matthew: "Матфея", mark: "Марка", luke: "Луки", john: "Иоанна", acts: "Деяния",
    romans: "Римлянам", "1corinthians": "1 Коринфянам", "2corinthians": "2 Коринфянам",
    galatians: "Галатам", ephesians: "Ефесянам", philippians: "Филиппийцам",
    colossians: "Колоссянам", "1thessalonians": "1 Фессалоникийцам", "2thessalonians": "2 Фессалоникийцам",
    "1timothy": "1 Тимофею", "2timothy": "2 Тимофею", titus: "Титу", philemon: "Филимону",
    hebrews: "Евреям", james: "Иакова", "1peter": "1 Петра", "2peter": "2 Петра",
    "1john": "1 Иоанна", "2john": "2 Иоанна", "3john": "3 Иоанна", jude: "Иуды", revelation: "Откровение"
  },
  es: {
    genesis: "Génesis", exodus: "Éxodo", leviticus: "Levítico", numbers: "Números",
    deuteronomy: "Deuteronomio", joshua: "Josué", judges: "Jueces", ruth: "Rut",
    "1samuel": "1 Samuel", "2samuel": "2 Samuel", "1kings": "1 Reyes", "2kings": "2 Reyes",
    "1chronicles": "1 Crónicas", "2chronicles": "2 Crónicas", ezra: "Esdras", nehemiah: "Nehemías",
    esther: "Ester", job: "Job", psalms: "Salmos", proverbs: "Proverbios",
    ecclesiastes: "Eclesiastés", songofsolomon: "Cantar de los Cantares", isaiah: "Isaías",
    jeremiah: "Jeremías", lamentations: "Lamentaciones", ezekiel: "Ezequiel", daniel: "Daniel",
    hosea: "Oseas", joel: "Joel", amos: "Amós", obadiah: "Abdías", jonah: "Jonás",
    micah: "Miqueas", nahum: "Nahúm", habakkuk: "Habacuc", zephaniah: "Sofonías",
    haggai: "Hageo", zechariah: "Zacarías", malachi: "Malaquías",
    matthew: "Mateo", mark: "Marcos", luke: "Lucas", john: "Juan", acts: "Hechos",
    romans: "Romanos", "1corinthians": "1 Corintios", "2corinthians": "2 Corintios",
    galatians: "Gálatas", ephesians: "Efesios", philippians: "Filipenses",
    colossians: "Colosenses", "1thessalonians": "1 Tesalonicenses", "2thessalonians": "2 Tesalonicenses",
    "1timothy": "1 Timoteo", "2timothy": "2 Timoteo", titus: "Tito", philemon: "Filemón",
    hebrews: "Hebreos", james: "Santiago", "1peter": "1 Pedro", "2peter": "2 Pedro",
    "1john": "1 Juan", "2john": "2 Juan", "3john": "3 Juan", jude: "Judas", revelation: "Apocalipsis"
  }
};

// Book name input variations (for parsing references)
const BOOK_INPUT_NAMES: Record<string, Record<string, string>> = {
  en: {
    genesis: "genesis", exodus: "exodus", leviticus: "leviticus", numbers: "numbers",
    deuteronomy: "deuteronomy", joshua: "joshua", judges: "judges", ruth: "ruth",
    "1 samuel": "1samuel", "2 samuel": "2samuel", "1 kings": "1kings", "2 kings": "2kings",
    "1 chronicles": "1chronicles", "2 chronicles": "2chronicles", ezra: "ezra", nehemiah: "nehemiah",
    esther: "esther", job: "job", psalms: "psalms", psalm: "psalms", proverbs: "proverbs",
    ecclesiastes: "ecclesiastes", "song of solomon": "songofsolomon", isaiah: "isaiah",
    jeremiah: "jeremiah", lamentations: "lamentations", ezekiel: "ezekiel", daniel: "daniel",
    hosea: "hosea", joel: "joel", amos: "amos", obadiah: "obadiah", jonah: "jonah",
    micah: "micah", nahum: "nahum", habakkuk: "habakkuk", zephaniah: "zephaniah",
    haggai: "haggai", zechariah: "zechariah", malachi: "malachi",
    matthew: "matthew", mark: "mark", luke: "luke", john: "john", acts: "acts",
    romans: "romans", "1 corinthians": "1corinthians", "2 corinthians": "2corinthians",
    galatians: "galatians", ephesians: "ephesians", philippians: "philippians",
    colossians: "colossians", "1 thessalonians": "1thessalonians", "2 thessalonians": "2thessalonians",
    "1 timothy": "1timothy", "2 timothy": "2timothy", titus: "titus", philemon: "philemon",
    hebrews: "hebrews", james: "james", "1 peter": "1peter", "2 peter": "2peter",
    "1 john": "1john", "2 john": "2john", "3 john": "3john", jude: "jude", revelation: "revelation"
  },
  ru: {
    бытие: "genesis", бытия: "genesis", исход: "exodus", исхода: "exodus", 
    левит: "leviticus", левита: "leviticus", числа: "numbers", чисел: "numbers",
    второзаконие: "deuteronomy", второзакония: "deuteronomy", 
    "иисус навин": "joshua", "иисуса навина": "joshua", судьи: "judges", судей: "judges", 
    руфь: "ruth", руфи: "ruth",
    "1 царств": "1samuel", "2 царств": "2samuel", "3 царств": "1kings", "4 царств": "2kings",
    "1 паралипоменон": "1chronicles", "2 паралипоменон": "2chronicles", 
    ездра: "ezra", ездры: "ezra", неемия: "nehemiah", неемии: "nehemiah",
    есфирь: "esther", есфири: "esther", иов: "job", иова: "job", 
    псалтирь: "psalms", псалом: "psalms", притчи: "proverbs", притч: "proverbs",
    екклесиаст: "ecclesiastes", екклесиаста: "ecclesiastes", 
    "песнь песней": "songofsolomon", исаия: "isaiah", исаии: "isaiah",
    иеремия: "jeremiah", иеремии: "jeremiah", 
    "плач иеремии": "lamentations", плач: "lamentations",
    иезекииль: "ezekiel", иезекииля: "ezekiel", даниил: "daniel", даниила: "daniel",
    осия: "hosea", осии: "hosea", иоиль: "joel", иоиля: "joel", 
    амос: "amos", амоса: "amos", авдий: "obadiah", авдия: "obadiah", 
    иона: "jonah", ионы: "jonah", михей: "micah", михея: "micah", 
    наум: "nahum", наума: "nahum", аввакум: "habakkuk", аввакума: "habakkuk", 
    софония: "zephaniah", софонии: "zephaniah", аггей: "haggai", аггея: "haggai", 
    захария: "zechariah", захарии: "zechariah", малахия: "malachi", малахии: "malachi",
    матфей: "matthew", матфея: "matthew", марк: "mark", марка: "mark",
    лука: "luke", луки: "luke", иоанн: "john", иоанна: "john", 
    деяния: "acts", деяний: "acts", римлянам: "romans",
    "1 коринфянам": "1corinthians", "2 коринфянам": "2corinthians",
    галатам: "galatians", ефесянам: "ephesians", филиппийцам: "philippians",
    колоссянам: "colossians", "1 фессалоникийцам": "1thessalonians", 
    "2 фессалоникийцам": "2thessalonians",
    "1 тимофею": "1timothy", "2 тимофею": "2timothy", титу: "titus", 
    филимону: "philemon", евреям: "hebrews", иакова: "james", иакову: "james",
    "1 петра": "1peter", "2 петра": "2peter",
    "1 иоанна": "1john", "2 иоанна": "2john", "3 иоанна": "3john", 
    иуды: "jude", откровение: "revelation", откровения: "revelation"
  },
  es: {
    génesis: "genesis", genesis: "genesis", éxodo: "exodus", exodo: "exodus",
    levítico: "leviticus", levitico: "leviticus", números: "numbers", numeros: "numbers",
    deuteronomio: "deuteronomy", josué: "joshua", josue: "joshua", jueces: "judges", rut: "ruth",
    "1 samuel": "1samuel", "2 samuel": "2samuel", "1 reyes": "1kings", "2 reyes": "2kings",
    "1 crónicas": "1chronicles", "1 cronicas": "1chronicles", 
    "2 crónicas": "2chronicles", "2 cronicas": "2chronicles",
    esdras: "ezra", nehemías: "nehemiah", nehemias: "nehemiah", ester: "esther", job: "job",
    salmos: "psalms", salmo: "psalms", proverbios: "proverbs", 
    eclesiastés: "ecclesiastes", ecclesiastes: "ecclesiastes",
    "cantar de los cantares": "songofsolomon", isaías: "isaiah", isaias: "isaiah",
    jeremías: "jeremiah", jeremias: "jeremiah", lamentaciones: "lamentations", 
    ezequiel: "ezekiel", daniel: "daniel",
    oseas: "hosea", joel: "joel", amós: "amos", amos: "amos", 
    abdías: "obadiah", abdias: "obadiah", jonás: "jonah", jonas: "jonah", 
    miqueas: "micah", nahúm: "nahum", nahum: "nahum",
    habacuc: "habakkuk", sofonías: "zephaniah", sofonias: "zephaniah", 
    hageo: "haggai", zacarías: "zechariah", zacarias: "zechariah", 
    malaquías: "malachi", malaquias: "malachi",
    mateo: "matthew", marcos: "mark", lucas: "luke", juan: "john", hechos: "acts",
    romanos: "romans", "1 corintios": "1corinthians", "2 corintios": "2corinthians",
    gálatas: "galatians", galatas: "galatians", efesios: "ephesians", 
    filipenses: "philippians", colosenses: "colossians", 
    "1 tesalonicenses": "1thessalonians", "2 tesalonicenses": "2thessalonians",
    "1 timoteo": "1timothy", "2 timoteo": "2timothy", tito: "titus", 
    filemón: "philemon", filemon: "philemon", hebreos: "hebrews", 
    santiago: "james", "1 pedro": "1peter", "2 pedro": "2peter",
    "1 juan": "1john", "2 juan": "2john", "3 juan": "3john", 
    judas: "jude", apocalipsis: "revelation"
  }
};

interface Verse {
  verse: number;
  text: string;
}

const Bible = () => {
  const { language, t } = useLanguage();
  const location = useLocation();
  const { toast } = useToast();
  const [selectedBook, setSelectedBook] = useState<typeof BIBLE_BOOKS.oldTestament[0] | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number>(1);
  const [verses, setVerses] = useState<Verse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);

  // Clear verses when language changes
  useEffect(() => {
    console.log('Language changed, clearing verses');
    setVerses([]);
    setError(null);
  }, [language]);

  // Reset state when Bible nav link is clicked or navigating without a chapter reference
  useEffect(() => {
    const state = location.state as { chapterReference?: string; reset?: boolean } | null;
    if (state?.reset) {
      console.log('Bible page reset triggered');
      setSelectedBook(null);
      setSelectedChapter(1);
      setVerses([]);
      setError(null);
      // Clear the reset flag
      window.history.replaceState({}, document.title);
    }
  }, [location.state, location.key]);

  // Handle chapter reference from navigation (e.g., "Ефесянам 1 гл.")
  useEffect(() => {
    const state = location.state as { chapterReference?: string; reset?: boolean } | null;
    if (state?.chapterReference && !state?.reset) {
      const match = state.chapterReference.match(/^(.+?)\s+(\d+)\s*(?:гл\.|ch\.|cap\.)?/i);
      if (match) {
        const bookInput = match[1].trim().toLowerCase();
        const chapter = parseInt(match[2]);
        
        console.log('Parsing chapter reference:', { original: state.chapterReference, bookInput, chapter, language });
        
        // Find the book key using the input variations map
        const inputMap = BOOK_INPUT_NAMES[language] || BOOK_INPUT_NAMES.en;
        let foundBookKey = inputMap[bookInput];
        
        // If not found in current language, try all other languages
        if (!foundBookKey) {
          for (const [lang, langInputMap] of Object.entries(BOOK_INPUT_NAMES)) {
            if (lang === language) continue;
            const key = langInputMap[bookInput];
            if (key) {
              foundBookKey = key;
              break;
            }
          }
        }
        
        console.log('Found book key:', foundBookKey, 'from input:', bookInput);
        
        if (foundBookKey) {
          // Find the book in BIBLE_BOOKS by its key
          const allBooks = [...BIBLE_BOOKS.oldTestament, ...BIBLE_BOOKS.newTestament];
          const book = allBooks.find(b => b.key === foundBookKey);
          
          console.log('Found book:', book);
          
          if (book) {
            setSelectedBook(book);
            setSelectedChapter(chapter);
          }
        }
      }
      // Clear the state after processing
      window.history.replaceState({}, document.title);
    }
  }, [location.state, language]);

  useEffect(() => {
    if (selectedBook) {
      console.log('Language changed to:', language);
      fetchChapter(selectedBook.id, selectedChapter);
    }
  }, [selectedBook, selectedChapter, language]);

  const fetchChapter = async (bookId: number, chapter: number) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Current language state:', language);
      const version = BIBLE_VERSIONS[language] || BIBLE_VERSIONS.en;
      const url = `https://bolls.life/get-chapter/${version}/${bookId}/${chapter}/`;
      
      console.log('Fetching Bible chapter with:', { language, version, bookId, chapter, url });
      
      const response = await fetch(url);
      
      console.log('Bible API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Bible API error response:', errorText);
        throw new Error(`Failed to fetch chapter: ${response.status}`);
      }

      const data = await response.json();
      console.log('Bible API data received (first verse):', data[0]);
      
      if (!Array.isArray(data)) {
        console.error('Invalid data format:', data);
        throw new Error('Invalid response format');
      }

      if (data.length === 0) {
        throw new Error('No verses found for this chapter');
      }

      const formattedVerses = data.map((item: any) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(item.text, 'text/html');
        doc.querySelectorAll('sup').forEach(sup => sup.remove());
        let text = doc.body.textContent || doc.body.innerText || '';
        text = text.replace(/([a-zA-Zа-яА-ЯёЁ]+)\d+/g, '$1');
        
        return {
          verse: item.verse,
          text: text.trim()
        };
      });

      console.log('Formatted verses count:', formattedVerses.length);
      setVerses(formattedVerses);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Could not load chapter';
      setError(errorMessage);
      console.error('Bible API error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBookSelect = (book: typeof BIBLE_BOOKS.oldTestament[0]) => {
    console.log('Book selected:', book.key);
    setVerses([]);  // Clear verses when selecting new book
    setError(null);
    setSelectedBook(book);
    setSelectedChapter(1);
  };

  const handlePreviousChapter = () => {
    if (selectedChapter > 1) {
      setSelectedChapter(selectedChapter - 1);
    }
  };

  const handleNextChapter = () => {
    if (selectedBook && selectedChapter < selectedBook.chapters) {
      setSelectedChapter(selectedChapter + 1);
    }
  };

  const getBookName = (bookKey: string) => {
    return BOOK_DISPLAY_NAMES[language]?.[bookKey] || BOOK_DISPLAY_NAMES.en[bookKey] || bookKey;
  };

  // Filter verses based on search query
  const filteredVerses = verses.filter(verse => 
    verse.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const clearSearch = () => {
    setSearchQuery("");
  };

  const getVoiceForLanguage = () => {
    const voices = window.speechSynthesis.getVoices();
    
    // Language to voice mapping
    const languageMap: { [key: string]: string[] } = {
      ru: ['ru-RU', 'ru'],
      es: ['es-ES', 'es-MX', 'es'],
      en: ['en-US', 'en-GB', 'en']
    };

    const preferredLangs = languageMap[language] || languageMap.en;
    
    // Try to find a voice matching the language
    for (const lang of preferredLangs) {
      const voice = voices.find(v => v.lang.startsWith(lang));
      if (voice) return voice;
    }
    
    return voices[0]; // Fallback to first available voice
  };

  const handleTextToSpeech = () => {
    if (!window.speechSynthesis) {
      toast({
        title: language === "ru" ? "Не поддерживается" : language === "es" ? "No soportado" : "Not supported",
        description: language === "ru" 
          ? "Ваш браузер не поддерживает синтез речи" 
          : language === "es" 
          ? "Su navegador no admite síntesis de voz" 
          : "Your browser doesn't support speech synthesis",
        variant: "destructive",
      });
      return;
    }

    if (isSpeaking) {
      // Stop speaking
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    if (filteredVerses.length === 0) {
      toast({
        title: language === "ru" ? "Нет текста" : language === "es" ? "Sin texto" : "No text",
        description: language === "ru" 
          ? "Выберите главу для чтения" 
          : language === "es" 
          ? "Selecciona un capítulo para leer" 
          : "Select a chapter to read",
        variant: "destructive",
      });
      return;
    }

    // Combine all verses into one text
    const fullText = filteredVerses.map(v => `${v.verse}. ${v.text}`).join(' ');
    
    const utterance = new SpeechSynthesisUtterance(fullText);
    const voice = getVoiceForLanguage();
    if (voice) utterance.voice = voice;
    utterance.lang = language === 'ru' ? 'ru-RU' : language === 'es' ? 'es-ES' : 'en-US';
    utterance.rate = 0.9; // Slightly slower for better comprehension
    
    utterance.onstart = () => {
      setIsSpeaking(true);
    };
    
    utterance.onend = () => {
      setIsSpeaking(false);
    };
    
    utterance.onerror = () => {
      setIsSpeaking(false);
      toast({
        title: language === "ru" ? "Ошибка" : language === "es" ? "Error" : "Error",
        description: language === "ru" 
          ? "Не удалось воспроизвести аудио" 
          : language === "es" 
          ? "No se pudo reproducir el audio" 
          : "Failed to play audio",
        variant: "destructive",
      });
    };
    
    speechSynthesisRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  // Cleanup on unmount or when chapter changes
  useEffect(() => {
    return () => {
      if (isSpeaking) {
        window.speechSynthesis.cancel();
      }
    };
  }, [selectedBook, selectedChapter]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
              <BookOpen className="h-8 w-8" />
              {language === "ru" ? "Библия" : language === "es" ? "Biblia" : "Bible"}
            </h1>
            <p className="text-lg text-muted-foreground">
              {language === "ru" 
                ? "Читайте Священное Писание" 
                : language === "es" 
                ? "Lee las Sagradas Escrituras" 
                : "Read the Holy Scriptures"}
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-6">
            {/* Books sidebar */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {language === "ru" ? "Книги Библии" : language === "es" ? "Libros de la Biblia" : "Bible Books"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="old" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="old">
                        {language === "ru" ? "Ветхий Завет" : language === "es" ? "Antiguo T." : "Old Test."}
                      </TabsTrigger>
                      <TabsTrigger value="new">
                        {language === "ru" ? "Новый Завет" : language === "es" ? "Nuevo T." : "New Test."}
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="old">
                      <ScrollArea className="h-[500px] pr-4">
                        <div className="space-y-1">
                          {BIBLE_BOOKS.oldTestament.map((book) => (
                            <Button
                              key={book.id}
                              variant={selectedBook?.id === book.id ? "default" : "ghost"}
                              className="w-full justify-start text-left"
                              onClick={() => handleBookSelect(book)}
                            >
                              <Book className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{getBookName(book.key)}</span>
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                    <TabsContent value="new">
                      <ScrollArea className="h-[500px] pr-4">
                        <div className="space-y-1">
                          {BIBLE_BOOKS.newTestament.map((book) => (
                            <Button
                              key={book.id}
                              variant={selectedBook?.id === book.id ? "default" : "ghost"}
                              className="w-full justify-start text-left"
                              onClick={() => handleBookSelect(book)}
                            >
                              <Book className="h-4 w-4 mr-2 flex-shrink-0" />
                              <span className="truncate">{getBookName(book.key)}</span>
                            </Button>
                          ))}
                        </div>
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>

            {/* Main reading area */}
            <div className="lg:col-span-3">
              {selectedBook ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <CardTitle className="text-2xl">
                        {getBookName(selectedBook.key)} {selectedChapter}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={handleTextToSpeech}
                          disabled={loading || filteredVerses.length === 0}
                          title={isSpeaking 
                            ? (language === "ru" ? "Остановить чтение" : language === "es" ? "Detener lectura" : "Stop reading")
                            : (language === "ru" ? "Прослушать главу" : language === "es" ? "Escuchar capítulo" : "Listen to chapter")
                          }
                        >
                          {isSpeaking ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={handlePreviousChapter}
                          disabled={selectedChapter === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {selectedChapter} / {selectedBook.chapters}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={handleNextChapter}
                          disabled={selectedChapter === selectedBook.chapters}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={
                          language === "ru" 
                            ? "Поиск в главе..." 
                            : language === "es" 
                            ? "Buscar en capítulo..." 
                            : "Search in chapter..."
                        }
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={clearSearch}
                          className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="space-y-4">
                        {[...Array(10)].map((_, i) => (
                          <Skeleton key={i} className="h-16 w-full" />
                        ))}
                      </div>
                    ) : error ? (
                      <div className="text-center py-12">
                        <p className="text-destructive">{error}</p>
                      </div>
                    ) : (
                      <ScrollArea className="h-[600px] pr-4">
                        {filteredVerses.length > 0 ? (
                          <div className="space-y-4">
                            {filteredVerses.map((verse) => (
                              <div key={verse.verse} className="flex gap-3">
                                <span className="font-bold text-primary flex-shrink-0 w-8">
                                  {verse.verse}
                                </span>
                                <p className="text-foreground leading-relaxed">{verse.text}</p>
                              </div>
                            ))}
                          </div>
                        ) : searchQuery ? (
                          <div className="text-center py-12">
                            <p className="text-muted-foreground">
                              {language === "ru" 
                                ? "Ничего не найдено" 
                                : language === "es" 
                                ? "No se encontraron resultados" 
                                : "No results found"}
                            </p>
                          </div>
                        ) : null}
                      </ScrollArea>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-24">
                    <div className="text-center text-muted-foreground">
                      <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                      <p className="text-lg">
                        {language === "ru" 
                          ? "Выберите книгу, чтобы начать чтение" 
                          : language === "es" 
                          ? "Selecciona un libro para comenzar a leer" 
                          : "Select a book to start reading"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Bible;
